import { search, searchClient, getUserToken } from "algolia-instant-search";

const { connectInfiniteHits } = instantsearch.connectors;
const INDEX_NAME = "shopify_products_manual_index_prod";

function toShopifyCdnWidth(url, width) {
  if (!url) return url;
  const [path, query = ""] = url.split("?", 2);
  const params = new URLSearchParams(query);
  const supportsQuality = !/\.(png|gif)$/i.test(path);
  params.set("width", String(width));
  if (supportsQuality) {
    params.set("quality", "80");
  } else {
    params.delete("quality");
  }
  return `${path}?${params.toString()}`;
}

const PRODUCT_CARD_IMAGE_WIDTHS = [220, 330, 440, 660, 880];
const PRODUCT_CARD_IMAGE_SIZES =
  "(min-width: 1200px) 20vw, (min-width: 992px) 25vw, (min-width: 750px) 33vw, 50vw";

function toShopifyCdnSrcset(url, widths) {
  if (!url) return "";
  return widths
    .map((width) => `${toShopifyCdnWidth(url, width)} ${width}w`)
    .join(", ");
}

function getAboveFoldProductCount() {
  if (window.matchMedia("(min-width: 1200px)").matches) return 5;
  if (window.matchMedia("(min-width: 992px)").matches) return 4;
  if (window.matchMedia("(min-width: 750px)").matches) return 3;
  return 2;
}
if (!customElements.get("algolia-infinite-hits")) {
  class AlgoliaInfiniteHits extends HTMLElement {
    catalogIds = this.dataset?.catalogIds?.split(",") ?? [];
    isB2b = this.dataset?.isB2b === "true";
    showDiscontinued = this.dataset?.showDiscontinued === "true";

    connectedCallback() {
      this.selectElements();

      window.__algoliaHits = window.__algoliaHits ?? new Map();
      this.sendConversionEvent = ({
        detail: { variantId, quantity, eventName },
      }) => {
        const entry = window.__algoliaHits.get(variantId);
        if (!entry) return;
        const { hit, sendEvent } = entry;
        sendEvent("conversion", hit, eventName, {
          eventSubtype: "addToCart",
          objectData: [
            { discount: hit.discount || 0, price: hit.price, quantity },
          ],
          value: hit.price * quantity,
          currency: "USD",
        });
      };
      window.addEventListener("algolia:atc", this.sendConversionEvent);

      const customInfiniteHits = connectInfiniteHits(
        this.renderInfiniteHits.bind(this),
      );

      search.addWidgets([
        customInfiniteHits({
          container: this.content,
        }),
      ]);
    }

    selectElements() {
      this.productCardTemplate = this.querySelector("template#product-card");
      this.emptyHeroTemplate = this.querySelector(
        "template#algolia-empty-hero-content",
      );
      this.emptyLayoutTemplate = this.querySelector(
        "template#algolia-empty-layout",
      );
      this.loadMoreButtonTemplate = this.querySelector(
        "template#load-more-button",
      );
      this.content = this.querySelector("#main-product-grid");
      this.noResultsText = this.content?.dataset.text || "No products found";
      this.emptyHeading = this.content?.dataset.emptyHeading || "";
      this.emptyShowQuery = this.content?.dataset.emptyShowQuery !== "false";
      this.emptySubheading = this.content?.dataset.emptySubheading || "";
      this.emptyAdditionalText =
        this.content?.dataset.emptyAdditionalText || "";
    }

    hasFitmentFilter() {
      return this.dataset.hasFitmentFilter === "true";
    }

    isHitDiscontinued(hit) {
      return (
        String(hit?.metafield_product_availability || "")
          .trim()
          .toLowerCase() === "discontinued"
      );
    }

    renderInfiniteHits(renderOptions, isFirstRender) {
      const { hits, showMore, isLastPage, widgetParams, results, sendEvent } =
        renderOptions;

      if (isFirstRender) {
        // Create load more button from template
        const loadMoreClone =
          this.loadMoreButtonTemplate.content.cloneNode(true);
        widgetParams.container.parentNode.insertBefore(
          loadMoreClone,
          widgetParams.container.nextSibling,
        );

        // Get reference to the button and add click handler
        const loadMoreContainer =
          widgetParams.container.parentNode.querySelector(".ymm-paginate");
        const button = loadMoreContainer.querySelector(".jsLoadMore");

        button.addEventListener("click", () => {
          showMore();
        });

        widgetParams.loadMoreContainer = loadMoreContainer;
        return;
      }

      if (!results || results.__isArtificial) {
        return;
      }

      const isEmpty = hits.length === 0;

      widgetParams.container.innerHTML = "";
      widgetParams.container.dataset.state = isEmpty ? "empty" : "results";
      // Keep the empty state full-width for every URL/history representation of
      // an active refinement. InstantSearch rewrites scalar query parameters to
      // bracketed arrays, so basing layout on the current URL made the same
      // result render differently before and after browser Back/Forward.
      this.setEmptyResultsLayout(isEmpty);

      if (isEmpty) {
        const emptyState = this.createEmptyState();
        widgetParams.container.appendChild(emptyState);
        this.updateFitmentEmptyState(emptyState);
      }

      hits.forEach((hit, index) => {
        const productCardClone =
          this.productCardTemplate.content.cloneNode(true);

        if (hit.variant_id) {
          window.__algoliaHits.set(hit.variant_id.toString(), {
            hit,
            queryID: results.queryID,
            sendEvent,
          });
        }

        // Populate product data
        const handle = hit.handle || "";
        const rawImageUrl = hit.images?.[0].url ?? hit.featured_image?.url ?? "";
        const imageUrl = toShopifyCdnWidth(rawImageUrl, 440);
        const imageSrcset = toShopifyCdnSrcset(
          rawImageUrl,
          PRODUCT_CARD_IMAGE_WIDTHS,
        );
        // The first row of cards is typically above the fold and can be the
        // page's LCP element, so it must not be lazy-loaded.
        const isAboveFold = index < getAboveFoldProductCount();

        const title = hit.title || hit.product_title || "";
        const sku = hit.sku || hit.variants?.[0]?.sku || "";
        const isDiscontinued =
          this.showDiscontinued && this.isHitDiscontinued(hit);
        if (isDiscontinued) {
          productCardClone.firstElementChild.classList.add(
            "product-card--discontinued",
          );
        }
        const regularPrice = hit.price ? `$${hit.price.toFixed(2)}` : "";
        let _catalogPrice;
        this.catalogIds.forEach((id) => {
          if (!hit[`price_catalog_${id}`]) return;
          _catalogPrice = _catalogPrice
            ? Math.min(_catalogPrice, hit[`price_catalog_${id}`])
            : hit[`price_catalog_${id}`];
        });
        const catalogPrice = _catalogPrice
          ? `$${_catalogPrice.toFixed(2)}`
          : "";
        const price = isDiscontinued
          ? "DISCONTINUED"
          : this.isB2b
            ? catalogPrice
            : regularPrice;
        const isQuickShip = hit.metafield_quick_ship === "1";
        const isExpertPick = hit.metafield_expert_pick === "1";
        const comparePrice = hit.compare_at_price
          ? `$${hit.compare_at_price.toFixed(2)}`
          : "";
        const variantSelected = hit.variant_id
          ? `?variant=${hit.variant_id}`
          : "";
        const queryIdParam = results.queryID
          ? `${variantSelected ? "&" : "?"}algolia_query_id=${results.queryID}`
          : "";
        const productUrl = `/products/${handle}${variantSelected}${queryIdParam}`;

        // Set product URL
        const productLinks =
          productCardClone.querySelectorAll("[data-product-url]");
        productLinks.forEach((link) => link.setAttribute("href", productUrl));

        const productTitleLink = productCardClone.querySelector(
          "[data-product-title-link]",
        );
        productTitleLink.setAttribute("href", productUrl);
        productTitleLink.textContent = title;

        const lazyScript = productCardClone.querySelector("lazy-script");
        lazyScript.setAttribute("data-url", productUrl);

        // Set product image
        const productImage = productCardClone.querySelector(
          "[data-product-image]",
        );
        productImage.setAttribute("src", imageUrl);
        if (imageSrcset) {
          productImage.setAttribute("srcset", imageSrcset);
          productImage.setAttribute("sizes", PRODUCT_CARD_IMAGE_SIZES);
        }
        productImage.setAttribute("alt", title);
        productImage.setAttribute("loading", isAboveFold ? "eager" : "lazy");
        if (index === 0) {
          productImage.setAttribute("fetchpriority", "high");
        }

        // Set SKU
        productCardClone.querySelector("[data-product-sku]").textContent = sku;

        // Set review stars and count from Algolia metafields
        const reviewsAverage = parseFloat(hit.metafield_reviews_average) || 0;
        const reviewsCount = hit.metafield_reviews_count || "0";
        const roundedAvg = Math.round(reviewsAverage);
        const fullStars = "★".repeat(roundedAvg);
        const emptyStars = "☆".repeat(5 - roundedAvg);

        const starsEl = productCardClone.querySelector("[data-review-stars]");
        const countEl = productCardClone.querySelector("[data-review-count]");
        if (isDiscontinued) {
          productCardClone.querySelector("[data-product-review]")?.remove();
        } else {
          starsEl.textContent = fullStars + emptyStars;
          countEl.textContent = `(${reviewsCount})`;
          countEl.setAttribute("role", "img");
          countEl.setAttribute(
            "aria-label",
            `${reviewsAverage} out 5 stars rating in total ${reviewsCount} reviews`,
          );
          countEl.setAttribute(
            "title",
            `${reviewsAverage} out 5 stars rating in total ${reviewsCount} reviews`,
          );
        }

        // Discontinued search cards use the image badge as their sole status.
        const productPrice = productCardClone.querySelector(".product-price");
        if (isDiscontinued) {
          productPrice?.remove();
        } else {
          productCardClone.querySelector("[data-product-price]").textContent =
            price;
        }

        // Set compare price
        const comparePriceEl = productCardClone.querySelector(
          "[data-product-compare-price]",
        );
        if (comparePriceEl && comparePrice && !isDiscontinued) {
          comparePriceEl.textContent = comparePrice;
        } else if (comparePriceEl) {
          comparePriceEl.remove();
        }

        // Discontinued replaces all fulfillment badges on search cards.
        if (isDiscontinued) {
          const labelGroup = productCardClone.querySelector(".label-group");
          if (labelGroup) {
            const discontinuedBadge = document.createElement("div");
            discontinuedBadge.className =
              "inline-block product-custom-label";
            discontinuedBadge.dataset.badge = "discontinued";
            discontinuedBadge.style.cssText =
              "--label-bg: var(--primary-color); --label-color: #fff; --label-border: var(--primary-color); width: 100%; text-align: center; white-space: nowrap;";
            discontinuedBadge.innerHTML =
              '<div class="flex flex-nowrap align-items-center justify-content-center"><span>DISCONTINUED</span></div>';
            labelGroup.style.width = "100%";
            labelGroup.replaceChildren(discontinuedBadge);
          }
        } else if (!isQuickShip) {
          productCardClone.querySelector('[data-badge="quick-ship"]')?.remove();
        }

        if (!isDiscontinued && !isExpertPick) {
          productCardClone
            .querySelector('[data-badge="expert-pick"]')
            ?.remove();
        }

        productCardClone.firstElementChild.addEventListener("click", () => {
          sendEvent("click", hit, "Hit Clicked");
        });

        widgetParams.container.appendChild(productCardClone);
      });

      try {
        const serializable = {};
        for (const [k, v] of window.__algoliaHits) {
          serializable[k] = { hit: v.hit, queryID: v.queryID };
        }
        sessionStorage.setItem("algolia_hits", JSON.stringify(serializable));
      } catch (e) {}

      if (widgetParams.loadMoreContainer) {
        widgetParams.loadMoreContainer.style.display =
          isLastPage || hits.length === 0 ? "none" : "block";
      }
    }

    disconnectedCallback() {
      this.setEmptyResultsLayout(false);
      window.removeEventListener("algolia:atc", this.sendConversionEvent);
    }

    setEmptyResultsLayout(enabled) {
      this.closest(".section-body")?.classList.toggle("empty-results", enabled);
      this.closest(".shopify-section-main-search-page")?.classList.toggle(
        "search-empty-results",
        enabled,
      );
    }

    createEmptyState() {
      const emptyState = document.createElement("div");
      emptyState.className = "algolia-no-results algolia-no-results--search";
      emptyState.setAttribute("role", "status");

      const shouldCreateStyledState =
        this.emptyHeading ||
        this.emptySubheading ||
        this.emptyAdditionalText ||
        this.hasFitmentFilter();

      if (!shouldCreateStyledState) {
        emptyState.textContent = this.noResultsText;
        return emptyState;
      }

      const hero = document.createElement("section");
      hero.className = "algolia-empty-hero";

      const headline = document.createElement("div");
      headline.className = "algolia-empty-hero__headline";

      const icon = document.createElement("span");
      icon.className = "algolia-empty-hero__icon";
      icon.setAttribute("aria-hidden", "true");
      icon.innerHTML = `
        <svg viewBox="0 0 24 24" focusable="false">
          <circle cx="10.5" cy="10.5" r="6.5"></circle>
          <path d="m16 16 4 4"></path>
        </svg>
      `;

      const copy = document.createElement("div");
      copy.className = "algolia-empty-hero__copy";

      const emptyHeading =
        this.emptyHeading ||
        (this.hasFitmentFilter() ? this.noResultsText : "");

      if (emptyHeading) {
        const heading = document.createElement("h2");
        heading.className = "algolia-no-results__heading";
        heading.textContent = emptyHeading;
        const query =
          new URLSearchParams(window.location.search).get("q") || "";
        if (query && this.emptyShowQuery) {
          const queryText = document.createElement("span");
          queryText.className = "algolia-no-results__query";
          queryText.textContent = ` "${query}"`;
          heading.appendChild(queryText);
        }
        copy.appendChild(heading);
      }

      if (this.emptySubheading) {
        const subheading = document.createElement("div");
        subheading.className = "algolia-no-results__detail";
        subheading.innerHTML = this.emptySubheading;
        copy.appendChild(subheading);
      }

      if (this.emptyAdditionalText) {
        const additionalText = document.createElement("div");
        additionalText.className = "algolia-no-results__additional";
        additionalText.innerHTML = this.emptyAdditionalText;
        copy.appendChild(additionalText);
      }

      headline.append(icon, copy);
      hero.appendChild(headline);

      if (this.emptyHeroTemplate) {
        hero.appendChild(this.emptyHeroTemplate.content.cloneNode(true));
      }

      emptyState.appendChild(hero);

      if (this.emptyLayoutTemplate) {
        emptyState.appendChild(
          this.emptyLayoutTemplate.content.cloneNode(true),
        );
        this.loadEmptyProductRails(emptyState);
      }

      return emptyState;
    }

    async updateFitmentEmptyState(emptyState) {
      if (this.dataset.hasFitmentFilter !== "true") return;

      try {
        this.fitmentOnlyEmptyPromise ??= searchClient
          .search([
            {
              indexName: INDEX_NAME,
              params: {
                query:
                  new URLSearchParams(window.location.search).get("q") || "",
                filters: this.dataset.baseFilters || "",
                hitsPerPage: 1,
                distinct: true,
                // WHY: only promise "clearing fitment would help" when the EXACT query
                // matches without the vehicle filter — not a loosened single-word match.
                // Mirrors the no-loosen policy applied to the main fitment-filtered query.
                removeWordsIfNoResults: "none",
              },
            },
          ])
          .then((response) => (response?.results?.[0]?.nbHits || 0) > 0);

        const isFitmentOnlyEmpty = await this.fitmentOnlyEmptyPromise;
        if (!isFitmentOnlyEmpty || !emptyState.isConnected) return;

        const copy = emptyState.querySelector(".algolia-empty-hero__copy");
        let heading = emptyState.querySelector(".algolia-no-results__heading");
        let detail = emptyState.querySelector(".algolia-no-results__detail");
        const additional = emptyState.querySelector(
          ".algolia-no-results__additional",
        );

        if (!heading && copy) {
          heading = document.createElement("h2");
          heading.className = "algolia-no-results__heading";
          copy.prepend(heading);
        }

        if (!detail && copy) {
          detail = document.createElement("div");
          detail.className = "algolia-no-results__detail";
          heading?.after(detail);
        }

        if (heading) {
          heading.textContent =
            this.dataset.fitmentEmptyHeading ||
            "No results due to the fitment filter";
        }
        if (detail) {
          detail.innerHTML =
            this.dataset.fitmentEmptySubheading ||
            "<p>To see results for this search, clear the vehicle fitment.</p>";
        }
        additional?.remove();

        if (copy && !copy.querySelector(".algolia-empty-hero__clear-fitment")) {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "algolia-empty-hero__clear-fitment";
          button.textContent =
            this.dataset.fitmentClearLabel || "Clear Vehicle Fitment";
          button.addEventListener("click", () => this.clearVehicleFitment());
          copy.appendChild(button);
        }
      } catch (error) {
        console.error("Fitment no-results check failed:", error);
      }
    }

    clearVehicleFitment() {
      try {
        sessionStorage.removeItem("yme_result_vehicle");
        sessionStorage.removeItem("yme_collection_hierarchy_menu");
        sessionStorage.removeItem("garageSelected");
        ["year", "make", "engine"].forEach((key) =>
          localStorage.removeItem(`ymm-${key}`),
        );
      } catch (error) {
        console.error("Error clearing vehicle fitment:", error);
      }

      const url = new URL(window.location.href);
      ["year", "make", "engine", "page"].forEach((key) =>
        url.searchParams.delete(key),
      );
      window.location.href = url.href;
    }

    loadEmptyProductRails(container) {
      const rails = [
        ...container.querySelectorAll("[data-empty-product-rail]"),
      ];
      if (!rails.length) return;

      rails.forEach(async (rail) => {
        const limit = this.getEmptyRailLimit(rail);
        if (!limit) {
          rail.closest("[data-empty-product-section]")?.remove();
          return;
        }

        try {
          const response = await this.loadEmptyProductRail(rail, limit);
          const hits = response?.results?.[0]?.hits || [];
          if (!hits.length) {
            rail.closest("[data-empty-product-section]")?.remove();
            return;
          }

          rail.replaceChildren(
            ...hits.map((hit) => this.createEmptyProductCard(hit)),
          );
        } catch (error) {
          console.error("No-results product rail failed to load:", error);
          rail.closest("[data-empty-product-section]")?.remove();
        }
      });
    }

    loadEmptyProductRail(rail, limit) {
      const source = rail.dataset.source || "search";
      if (source === "specific_products") {
        return this.loadSpecificSkuRail(rail, limit);
      }

      if (source === "search") {
        return searchClient.search([
          {
            indexName: INDEX_NAME,
            params: {
              query: rail.dataset.query || "",
              // Vehicle-scope the search rail too (it previously carried no filters at all),
              // so a fitment no-results page never surfaces unfit product cards.
              filters: this.getEmptyRailFilters(),
              hitsPerPage: limit,
              clickAnalytics: true,
              userToken: getUserToken(),
            },
          },
        ]);
      }

      const request = {
        indexName: INDEX_NAME,
        model: source === "trending" ? "trending-items" : "related-products",
        threshold: 0,
        maxRecommendations: limit,
        queryParameters: {
          filters: this.getEmptyRailFilters(),
          userToken: getUserToken(),
        },
        fallbackParameters: {
          filters: this.getEmptyRailFilters(),
        },
      };

      if (source === "recommended") {
        if (!rail.dataset.recommendationObjectId) {
          return Promise.resolve({ results: [] });
        }
        request.objectID = rail.dataset.recommendationObjectId;
      }

      return searchClient.getRecommendations({ requests: [request] });
    }

    async loadSpecificSkuRail(rail, limit) {
      const skus = this.getSpecificRailSkus(rail).slice(0, limit);
      if (!skus.length) return { results: [{ hits: [] }] };

      const response = await searchClient.search(
        skus.map((sku) => ({
          indexName: INDEX_NAME,
          params: {
            query: sku,
            filters: this.getEmptyRailFilters(),
            hitsPerPage: 10,
            clickAnalytics: true,
            userToken: getUserToken(),
          },
        })),
      );

      const hits = skus
        .map((sku, index) =>
          this.findExactSkuHit(response?.results?.[index]?.hits || [], sku),
        )
        .filter(Boolean);

      return { results: [{ hits }] };
    }

    getSpecificRailSkus(rail) {
      const seen = new Set();
      return (rail.dataset.skus || "")
        .split(",")
        .map((sku) => sku.trim())
        .filter((sku) => {
          const key = sku.toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 4);
    }

    findExactSkuHit(hits, sku) {
      const normalizedSku = sku.toLowerCase();
      return hits.find((hit) =>
        [hit.sku, ...(hit.variants || []).map((variant) => variant?.sku)]
          .filter(Boolean)
          .some((hitSku) => hitSku.toLowerCase() === normalizedSku),
      );
    }

    getEmptyRailFilters() {
      const filters = [
        "status:ACTIVE",
        "NOT metafield_seo_hidden:1",
        "NOT metafield_product_availability:Discontinued",
      ];
      const catalogIds = this.catalogIds.filter(Boolean);
      if (catalogIds.length) {
        filters.push(`catalog_ids:${catalogIds.join(",")}`);
      }
      // WHY: empty-state recommendation rails must respect the active vehicle fitment,
      // else a fitment no-results page shows unfit cards. fitmentFilter is the raw
      // "AND (vehicles:... OR universal:true)" string from the parent — strip the leading AND.
      const fitment = (this.dataset.fitmentFilter || "").replace(/^\s*AND\s+/, "");
      if (fitment) filters.push(fitment);
      return filters.join(" AND ");
    }

    getEmptyRailLimit(rail) {
      const desktopLimit = Number(rail.dataset.desktopLimit) || 4;
      const mobileLimit = Number(rail.dataset.mobileLimit) || 2;
      const showMobile = rail.dataset.showMobile !== "false";

      if (window.matchMedia("(max-width: 749px)").matches) {
        return showMobile ? mobileLimit : 0;
      }

      return desktopLimit;
    }

    createEmptyProductCard(hit) {
      const card = document.createElement("article");
      card.className = "algolia-empty-product-card";

      const handle = hit.handle || "";
      const rawImageUrl = hit.images?.[0]?.url ?? hit.featured_image?.url ?? "";
      const imageUrl = toShopifyCdnWidth(rawImageUrl, 440);
      const imageSrcset = toShopifyCdnSrcset(
        rawImageUrl,
        PRODUCT_CARD_IMAGE_WIDTHS,
      );
      const title = hit.title || hit.product_title || "";
      const price = hit.price ? `$${hit.price.toFixed(2)}` : "";
      const productUrl = `/products/${handle}`;

      card.innerHTML = `
        <a class="algolia-empty-product-card__image" href="${productUrl}">
          <img src="${imageUrl}" ${imageSrcset ? `srcset="${imageSrcset}" sizes="${PRODUCT_CARD_IMAGE_SIZES}"` : ""} alt="${this.escapeHtml(title)}" loading="lazy" width="220" height="220">
        </a>
        <a class="algolia-empty-product-card__title" href="${productUrl}">${this.escapeHtml(title)}</a>
        <div class="algolia-empty-product-card__price">${this.escapeHtml(price)}</div>
      `;

      return card;
    }

    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }
  }
  customElements.define("algolia-infinite-hits", AlgoliaInfiniteHits);
}
